import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ChevronRight, Heart, Clock, MapPin, Utensils, Coffee, Wifi } from 'lucide-react';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';
function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}
const services = [{
  title: 'Individualised Care',
  slug: 'individualized-care',
  category: 'Main Services',
  desc: 'We provide an exceptional blend of quality nursing and wellness services designed for unique needs.',
  image: 'https://elisabethhouse.net/wp-content/uploads/2020/04/4-760x428.png',
  icon: Heart
}, {
  title: 'Respite Care',
  slug: 'respite-care',
  category: 'Main Services',
  desc: 'It is a range of healthcare services that can be given in your home for short-term stays.',
  image: 'https://elisabethhouse.net/wp-content/uploads/2020/04/senior-man-using-laptop-while-sitting-on-sofa-KZR6783-scaled.jpg',
  icon: Clock
}, {
  title: 'Trips and Outing activities',
  slug: 'trips-and-outing-activities',
  category: 'Main Services',
  desc: 'We provide transportation for our residents to curated cultural experiences and social excursions.',
  image: 'https://elisabethhouse.net/wp-content/uploads/2020/04/front-view-of-senior-friends-interacting-with-each-JURYHC9-scaled.jpg',
  icon: MapPin
}, {
  title: 'Assisting with Eating',
  slug: 'assisting-with-eating',
  category: 'Main Services',
  desc: 'We provide home made and delicious meals with dignified support for gourmet dining experiences.',
  image: 'https://elisabethhouse.net/wp-content/uploads/2020/03/pic1-760x428.jpg',
  icon: Utensils
}, {
  title: 'Dressing',
  slug: 'dressing',
  category: 'Main Services',
  desc: "Elisabeth's House, Hagerstown, offers daily activities assistance and professional support with routines.",
  image: 'https://elisabethhouse.net/wp-content/uploads/2020/03/pic2-760x428.jpg',
  icon: Coffee
}, {
  title: 'Telephone, cable TV, wifi available',
  slug: 'electronics-and-wifi',
  category: 'Main Services',
  desc: 'Computer and internet access are available as well as a 24/7 on call nurse, grooming, and entertainment.',
  image: 'https://elisabethhouse.net/wp-content/uploads/2020/03/pic5-760x428.jpg',
  icon: Wifi
}];
export const Services = () => {
  return <div className="min-h-screen bg-white" data-id="element-556">
      {/* Page Header */}
      <section className="relative h-[50vh] min-h-[400px] flex items-center overflow-hidden pt-24" data-id="element-557">
        <div className="absolute inset-0 z-0" data-id="element-558">
          <img src="https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?auto=format&fit=crop&q=80&w=2000" alt="Elisabeth House Services" className="w-full h-full object-cover" data-id="element-559" />
          <div className="absolute inset-0 bg-gradient-to-r from-charcoal/80 via-charcoal/50 to-charcoal/20" data-id="element-560" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 w-full" data-id="element-561">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          ease: 'easeOut'
        }} data-id="element-562">
            <div className="flex items-center space-x-3 text-white/60 text-xs uppercase tracking-[0.3em] mb-8" data-id="element-563">
              <a href="/" className="hover:text-warmCoral transition-colors" data-id="element-564">
                Home
              </a>
              <span className="w-1 h-1 bg-warmCoral rounded-full" data-id="element-565" />
              <span className="text-white" data-id="element-566">All Services</span>
            </div>

            <div className="relative inline-block" data-id="element-567">
              <h1 className="text-white text-5xl md:text-7xl font-serif leading-none mb-4" data-id="element-568">
                All Services
              </h1>
              <motion.span initial={{
              opacity: 0,
              x: -20
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: 0.5,
              duration: 0.8
            }} className="absolute -top-16 -right-8 text-warmCoral font-accent text-4xl md:text-5xl rotate-[-12deg] hidden md:block" data-id="element-569">
                Our Care
              </motion.span>
            </div>

            <p className="text-white/80 text-xl md:text-2xl max-w-2xl font-light leading-relaxed mt-8 border-l-2 border-warmCoral pl-8" data-id="element-570">
              Comprehensive clinical support seamlessly integrated into a
              lifestyle of abundance and dignity.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-24 px-4 md:px-8 bg-white" data-id="element-571">
        <div className="max-w-7xl mx-auto" data-id="element-572">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-573">
            {services.map((service, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: i * 0.1
          }} viewport={{
            once: true
          }} className="group bg-white rounded-[40px] overflow-hidden border border-warmGray/10 shadow-sm hover:shadow-2xl transition-all duration-500" data-id="element-574">
                <div className="aspect-[16/10] overflow-hidden relative" data-id="element-575">
                  <img src={service.image} alt={service.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" data-id="element-576" />
                  <div className={cn('absolute top-6 left-6 bg-white/90 backdrop-blur-md p-3 rounded-2xl shadow-lg', i % 2 === 0 ? 'text-warmCoral' : 'text-lightBlue')} data-id="element-577">
                    <service.icon className="w-6 h-6" data-id="element-578" />
                  </div>
                </div>

                <div className="p-10" data-id="element-579">
                  <span className={cn('tracking-widest uppercase text-xs font-medium mb-4 block', i % 2 === 0 ? 'text-warmCoral' : 'text-lightBlue')} data-id="element-580">
                    {service.category}
                  </span>
                  <h3 className={cn('text-charcoal text-2xl font-serif mb-4 transition-colors', i % 2 === 0 ? 'group-hover:text-warmCoral' : 'group-hover:text-lightBlue')} data-id="element-581">
                    {service.title}
                  </h3>
                  <p className="text-charcoal/60 leading-relaxed mb-8 line-clamp-3" data-id="element-582">
                    {service.desc}
                  </p>
                  <Link to={`/services/${service.slug}`} className="flex items-center space-x-2 text-charcoal font-medium group/btn" data-id="element-583">
                    <span data-id="element-584">Learn more</span>
                    <ChevronRight className="w-4 h-4 group-hover/btn:translate-x-1 transition-transform" data-id="element-585" />
                  </Link>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="pb-24 px-4 md:px-8" data-id="element-586">
        <div className="max-w-7xl mx-auto" data-id="element-587">
          <div className="bg-warmDark rounded-[60px] p-12 md:p-20 text-center relative overflow-hidden" data-id="element-588">
            <div className="absolute inset-0 bg-gradient-to-r from-warmCoral/10 to-transparent opacity-50" data-id="element-589" />
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="relative z-10" data-id="element-590">
              <h2 className="text-white text-4xl md:text-5xl font-serif mb-8" data-id="element-591">
                Ready to experience the difference?
              </h2>
              <p className="text-white/60 text-xl max-w-2xl mx-auto mb-12" data-id="element-592">
                Contact our care coordinators today to learn how we can tailor
                our services to your family's unique needs.
              </p>
              <button className="bg-warmCoral text-white px-12 py-5 rounded-full text-lg font-medium hover:bg-white hover:text-charcoal transition-all shadow-2xl" data-id="element-593">
                Schedule a Private Tour
              </button>
            </motion.div>
          </div>
        </div>
      </section>
    </div>;
};